import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchPatients,
  fetchPatientById,
  fetchPatientCases,
  modifyPatientDetails,
  fetchPatientSchemes,
  addPatientDetails,
  postSchemeToGetPatient,
  getPatientBasicDetails,
  getPreAuthQueryData,
  downloadReport,
  fetchReferralList,
  deleteUploadedDocument,
  getQuestionsDeficiencyCheck,
  getSavedDocumentDeficiencyCheck,
  postQuestionDeficiencyCheck,
  fetchPatientCasesWithVisits,
  fetchPatientDetailsUHID,
  fetchAllDetailsCount,
  fetchPatientsPL,
  fetchAllVisitStages,
  fetchAllVisitTypes,
  fetchAllStates,
  fetchFilteredVisitStages,
  fetchTimeline,
  getShiftPatient,
  postShiftPatient,
  updateShiftPatient,
  getPatientBasicDetails2,
  enhancementService,
  downloadReportNew,
  postBeneficiaryDetails,
  getPatientDetailsByBeneficiaryId,
  getPatientListPendingCounts,
  newFetchPatientsPL,
  fetchPatientTatColorCoding,
  mergePatientsWithTatColorCoding,
  downloadSubmissionSettlementReport,
} from "../apis/patientService";
import {
  IPatient,
  IAllPatientFetchResponse,
  IPatientState,
  IPatientFetchResponse,
  IPatientFilter,
  IPatientCasesFetchResponse,
  IPatientSchemeList,
  IFetchPatientBasicDetails,
  IFetchPatientDetailsFromPortalResponse,
  IPostPatientDetailsResponse,
  IPatientFetchDetailsFromPortal,
  IPatientPreAuthQueryGetDetails,
  IDeficiencyCheck,
  IPatientCasesWithVisitsFetchResponse,
  IVisitStage,
  IVisitStageResponse,
  IVisitType,
  IState,
  IStageCount,
  IPatientFetchDetailsFromAyushBPortal,
  IPostBeneficiaryDetails,
} from "../interfaces/IPatient";
import { readOcrDoc, uploadDocs } from "../apis/caseService";
import { IDocumentFields } from "../interfaces/ICase";
import { errorMessage } from "../utils/notificationUtils";
import { fetchAllSchemes } from "../apis/schemeService";
import { IScheme, ISchemeResponse } from "../interfaces/IScheme";
import { LIST_DEFAULT_PAGE_SIZE } from "../utils/constants";
import { postSchemeToGetPatientAyushB } from "../apis/ayushmanBService";
import {
  fetchHealthIndiaBeneficiaryDetails,
  HealthIndiaPatientFetchDetails,
} from "../apis/SchemeService/HEALTHINDIA/healthIndiaService";
import {
  fetchHeritageBeneficiaryDetails,
  HeritagePatientFetchDetails,
} from "../apis/SchemeService/HERITAGE/heritageService";
import {
  fetchStarHealthBeneficiaryDetails,
  StarHealthPatientFetchDetails,
} from "../apis/SchemeService/STARHEALTH/starHealthService";
import {
  fetchFhplBeneficiaryVerification,
  FhplPatientFetchDetails,
} from "../apis/SchemeService/FHPL/fhplService";

const initialState: IPatientState = {
  totalAvailablePatients: 0,
  patients: {},
  allPatients: [],
  ocrDetails: null,
  ocrDetailsOfReferral: null,
  documentDetails: null,
  patientDetails: null,
  selectedPatient: null,
  isFetchingVisitStages: false,
  isFetchingVisitTypes: false,
  isFetchingSchemes: false,
  isFetchingStates: false,
  isFetchingPatientById: false,
  isFetchingAllPatients: false,
  isFetchingPatientCases: false,
  isPostPatientDetails: false,
  isOcrFetchingPatient: false,
  isFetchingPatientDetails: false,
  patientListPendingCounts: null,
  uhidNo: "",
  error: null,
  referrals: {},
  patientToCasesMap: {},
  patientSchemes: null,
  patientFilters: {
    endDate: "",
    orderBy: "DESC",
    direction: "DESC",
    pageNumber: 1,
    pageSize: 50,
    searchTerm: null,
    startDate: "",
    state: null,
    scheme: null,
    visitType: null,
    status: true,
    stage: null,
    selectedDateType: "DOA",
  },
  retainedPatientFilter: {
    selectedDatePreset: "lastWeek",
    primarySchemeSelections: [],
    secondarySchemeSelections: [],
  },
  pageToPatientIdMap: {},
  fetchBasicDetails: null,
  fetchBasicDetails2: null,
  fetchDetails: undefined,
  isLoading: false,
  isFetchingTimeLine: false,
  preAuthQueryDetails: null,
  isFetchingPreAuthQueryDetails: false,
  selectedFile: null,
  isDownloadingReport: false,
  deficiencyCheckQuestions: null,
  casesWithAllVisitsOfPatient: {},
  isFetchingPatientCasesWithVisits: false,
  allDetailsCount: null,
  visitStages: {},
  visitTypes: {},
  schemes: {},
  states: {},
  stagesCount: {},
  timelineDetails: [],
  shiftPatientData: [],
  schemeMetaData: null,
};

export const getPatients = createAsyncThunk(
  "patients/getPatients",
  async ({
    filters,
    accessToken,
    healthCareId,
  }: {
    filters: IPatientFilter;
    accessToken: string;
    healthCareId: number;
  }) => {
    // handling 0 base indexing on server
    filters = { ...filters, pageNumber: filters.pageNumber - 1 };
    const response = await fetchPatients(filters, healthCareId);
    return response;
  },
);

export const getPatientsPL = createAsyncThunk(
  "patients/getPatientsPL",
  async ({
    filters,
    healthCareId,
  }: {
    filters: IPatientFilter;
    healthCareId: number;
  }) => {
    // handling 0 base indexing on server
    filters = { ...filters, pageNumber: filters.pageNumber - 1 };
    const [response, tatList] = await Promise.all([
      newFetchPatientsPL(filters, healthCareId),
      fetchPatientTatColorCoding(filters, healthCareId),
    ]);
    return {
      ...response,
      patientResponseList: mergePatientsWithTatColorCoding(
        response.patientResponseList,
        tatList,
      ),
    };
  },
);

export const getAllVisitStages = createAsyncThunk(
  "patients/getAllVisitStages",
  async () => {
    const response = await fetchFilteredVisitStages();
    return response;
  },
);

export const getAllSchemes = createAsyncThunk(
  "patients/getAllSchemes",
  async () => {
    const response = await fetchAllSchemes();
    return response;
  },
);

export const getAllStates = createAsyncThunk(
  "patients/getAllStates",
  async () => {
    const response = await fetchAllStates();
    return response;
  },
);

export const getAllVisitTypes = createAsyncThunk(
  "patients/getAllVisitTypes",
  async () => {
    const response = await fetchAllVisitTypes();
    return response;
  },
);

export const getPatientCases = createAsyncThunk(
  "patients/getPatientCases",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchPatientCases(id);
    return response;
  },
);

export const getPatientCasesWithVisits = createAsyncThunk(
  "patients/getPatientCasesWithVisits",
  async ({
    id,
    billType,
    accessToken,
  }: {
    id: number;
    billType: string;
    accessToken: string;
  }) => {
    const response = await fetchPatientCasesWithVisits(id, billType);
    return response;
  },
);

export const getPatientReferral = createAsyncThunk(
  "patients/getPatientReferral",
  async (patientId: number) => {
    const response = await fetchReferralList(patientId);
    return response;
  },
);

export const getPatientListCounts = createAsyncThunk(
  "patients/getPatientListCounts",
  async (hospitalId: number) => {
    const response = await getPatientListPendingCounts(hospitalId);
    return response;
  },
);

export const getPatientSchemes = createAsyncThunk(
  "patients/getPatientSchemes",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchPatientSchemes(id);
    return response;
  },
);

export const getPatientById = createAsyncThunk(
  "patients/getPatientById",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchPatientById(id);
    return response;
  },
);

export const downloadReportScheme = createAsyncThunk(
  "patients/downloadReportScheme",
  async (formData: any) => {
    await downloadReport(formData);
  },
);

export const downloadReportSchemeNew = createAsyncThunk(
  "patients/downloadReportSchemeNew",
  async (formData: any) => {
    // await downloadReportNew(formData);
    await downloadSubmissionSettlementReport(formData);
  },
);

export const getDeficiencyQuestions = createAsyncThunk(
  "patients/getDeficiencyQuestions",
  async (documentId: number) => {
    const response = await getQuestionsDeficiencyCheck(documentId);
    return response;
  },
);

export const getDeficiencyQuestionData = createAsyncThunk(
  "patients/getDeficiencyQuestionData",
  async (documentId: number) => {
    const response = await getSavedDocumentDeficiencyCheck(documentId);
    return response;
  },
);

export const getTimelineDetails = createAsyncThunk(
  "patients/getTimelineDetails",
  async ({ visitId, caseId }: { visitId: number; caseId: number }) => {
    const response = await fetchTimeline(visitId, caseId);
    return response;
  },
);

export const readOcrDocs = createAsyncThunk(
  "patients/readOcrDocs",
  async ({
    formData,
    accessToken,
  }: {
    formData: FormData;
    accessToken: string;
  }) => {
    const response = await readOcrDoc(formData, accessToken);
    return response;
  },
);

export const fetchPatientDetailsFromPortal = createAsyncThunk(
  "patients/fetchPatientDetailsFromPortal",
  async ({
    formData,
    accessToken,
  }: {
    formData: IPatientFetchDetailsFromPortal;
    accessToken: string;
  }) => {
    const response = await postSchemeToGetPatient(formData);
    return response;
  },
);

export const postBeneficiaryDetailsForScheme = createAsyncThunk(
  "patients/postBeneficiaryDetailsForScheme",
  async (reqPayload: IPostBeneficiaryDetails) => {
    const response = await postBeneficiaryDetails(reqPayload);
    return response;
  },
);

export const fetchPatientDetailsByBeneficiaryId = createAsyncThunk(
  "patients/fetchPatientDetailsByBeneficiaryId",
  async (beneficiaryId: string) => {
    const response = await getPatientDetailsByBeneficiaryId(beneficiaryId);
    return response;
  },
);

export const fetchPatientDetailsFromHealthIndiaPortal = createAsyncThunk(
  "patients/fetchPatientDetailsFromHealthIndiaPortal",
  async (payloadData: HealthIndiaPatientFetchDetails) => {
    const response = await fetchHealthIndiaBeneficiaryDetails(payloadData);
    return response;
  },
);

export const fetchPatientDetailsFromHeritagePortal = createAsyncThunk(
  "patients/fetchPatientDetailsFromHeritagePortal",
  async (payloadData: HeritagePatientFetchDetails) => {
    const response = await fetchHeritageBeneficiaryDetails(payloadData);
    return response;
  },
);

export const fetchPatientDetailsFromStarHealthPortal = createAsyncThunk(
  "patients/fetchPatientDetailsFromStarHealthPortal",
  async (payloadData: StarHealthPatientFetchDetails) => {
    const response = await fetchStarHealthBeneficiaryDetails(payloadData);
    return response;
  },
);

export const fetchPatientDetailsFromFhplPortal = createAsyncThunk(
  "patients/fetchPatientDetailsFromFhplPortal",
  async (payloadData: FhplPatientFetchDetails) => {
    const response = await fetchFhplBeneficiaryVerification(payloadData);
    return response;
  },
);

export const fetchPatientDetailsFromAyushBPortal = createAsyncThunk(
  "patients/fetchPatientDetailsFromAyushBPortal",
  async ({
    payloadData,
    accessToken,
    hospitalId,
  }: {
    payloadData: IPatientFetchDetailsFromAyushBPortal;
    accessToken: string;
    hospitalId: any;
  }) => {
    const response = await postSchemeToGetPatientAyushB(
      payloadData,
      hospitalId,
    );
    return response;
  },
);

export const UpdatePatientDetails = createAsyncThunk(
  "patients/UpdatePatientDetails",
  async ({
    id,
    formValues,
    accessToken,
  }: {
    id: number;
    formValues: object;
    accessToken: string;
  }) => {
    const response = await modifyPatientDetails(id, formValues);
    return response;
  },
);

export const getBasicDetails = createAsyncThunk(
  "patients/getBasicDetails",
  async ({
    id,
    documentType,
  }: {
    id: number;
    documentType?: Array<string | number>;
  }) => {
    const response = await getPatientBasicDetails(id, documentType);
    return response;
  },
);
export const getBasicDetails2 = createAsyncThunk(
  "patients/getBasicDetails2",
  async ({ id }: { id: number }) => {
    const response = await getPatientBasicDetails2(id);
    return response;
  },
);
export const postPatientDetails = createAsyncThunk(
  "patients/postPatientDetails",
  async ({
    formValues,
    accessToken,
  }: {
    formValues: object;
    accessToken: string;
  }) => {
    const response = await addPatientDetails(formValues);
    return response;
  },
);

export const getPatientDetailsUsingUHID = createAsyncThunk(
  "patients/getPatientDetailsUsingUHID",
  async ({
    formData,
    schemeMetaDataId,
  }: {
    formData: any;
    schemeMetaDataId: number;
  }) => {
    const response = await fetchPatientDetailsUHID(formData, schemeMetaDataId);
    return response;
  },
);

export const fetchShiftPatientDetails = createAsyncThunk(
  "patients/fetchShiftPatientDetails",
  async ({ caseId, visitId }: { caseId: number; visitId: number }) => {
    const response = await getShiftPatient(caseId, visitId);
    return response;
  },
);

export const submitShiftPatientDetails = createAsyncThunk(
  "patients/submitShiftPatientDetails",
  async (formData: any) => {
    const response = await postShiftPatient(formData);
    return response;
  },
);

export const updateShiftPatientDetails = createAsyncThunk(
  "patients/updateShiftPatientDetails",
  async ({
    formData,
    visitShiftId,
  }: {
    formData: any;
    visitShiftId: number;
  }) => {
    const response = await updateShiftPatient(formData, visitShiftId);
    return response;
  },
);

export const getPreAuthQueryDetails = createAsyncThunk(
  "patients/getPreAuthQueryDetails",
  async ({
    data,
    caseId,
    schemeMetaDataId,
    visitInfoId,
  }: {
    data: string;
    caseId: number | undefined;
    schemeMetaDataId: number | undefined;
    visitInfoId: number | undefined;
  }) => {
    const response = await getPreAuthQueryData(
      data,
      caseId,
      visitInfoId,
      schemeMetaDataId,
    );
    return response;
  },
);

export const updateDocuments = createAsyncThunk(
  "patients/getReferralById",
  async (formValues: any) => {
    const response = await deleteUploadedDocument(formValues);
    return response;
  },
);

export const postDeficiencyQuestion = createAsyncThunk(
  "patients/postDeficiencyQuestion",
  async (formValues: any) => {
    const response = await postQuestionDeficiencyCheck(formValues);
    return response;
  },
);

export const uploadDocument = createAsyncThunk(
  "patients/uploadDocument",
  async (formValues: any) => {
    const res = await uploadDocs(formValues);
    return res;
  },
);

export const getAllDetailsCount = createAsyncThunk(
  "patients/getAllDetailsCount",
  async (healthCareId: number) => {
    const res = await fetchAllDetailsCount(healthCareId);
    return res;
  },
);

export const getSchemeMetaData = createAsyncThunk(
  "patients/getSchemeMetaData",
  async (schemeMetaDataId: number) => {
    const res = await enhancementService.getPortalMetaData(schemeMetaDataId);
    return res;
  },
);

export const processPatientsPLData = createAsyncThunk(
  "patients/processPatientsPLData",
  async ({ patients, pageNumber }: { patients: any[]; pageNumber: number }) => {
    // Heavy processing here - fully async!
    const processed: any = { patientsMap: {}, pageToIdMap: {} };

    patients.forEach((patient) => {
      if (patient?.visitId) {
        processed.patientsMap[patient.visitId] = patient;
        if (!processed.pageToIdMap[pageNumber])
          processed.pageToIdMap[pageNumber] = [];
        if (!processed.pageToIdMap[pageNumber].includes(patient.visitId)) {
          processed.pageToIdMap[pageNumber].push(patient.visitId);
        }
      }
    });

    return processed;
  },
);

const patientSlice = createSlice({
  name: "patients",
  initialState,
  reducers: {
    updateSelectedPatient: (state, action: PayloadAction<IPatient>) => {
      state.selectedPatient = action.payload;
    },
    resetPatientToCasesMap: (state) => {
      state.patientToCasesMap = {};
    },

    setPatientFilters: (state, action: PayloadAction<IPatientFilter>) => {
      state.patientFilters = action.payload;
    },
    setRetainedPatientFilters: (state, action) => {
      state.retainedPatientFilter = action.payload;
    },
    clearPageToPatientIdMap: (state) => {
      state.pageToPatientIdMap = {};
    },
    clearPatientDetails: (state) => {
      state.patientDetails = null;
    },
    clearOcrDetails: (state) => {
      state.ocrDetails = null;
      state.documentDetails = null;
    },
    addPatientDetail: (state, action) => {
      state.patientDetails = action.payload;
    },
    clearFetchBasicDetail: (state, action) => {
      state.fetchBasicDetails = null;
      state.fetchBasicDetails2 = null;
    },

    clearDeficiencyCheck: (state) => {
      state.deficiencyCheckQuestions = null;
    },
    clearPatientReferrals: (state) => {
      state.referrals = {};
    },
    clearOcrDetailsOfReferral: (state) => {
      state.ocrDetailsOfReferral = null;
    },
    clearUhiNo: (state) => {
      state.uhidNo = "";
    },
    swapReadOcrDetails: (state, action) => {
      state.ocrDetails = action.payload;
    },
    updatePreAuthQueryField: (state, action) => {
      const { index, id, value } = action.payload;
      if (
        (state.preAuthQueryDetails &&
          state.preAuthQueryDetails.ihxQueriesResponses) ||
        state.preAuthQueryDetails?.cghsQueriesResponse
      ) {
        if (state?.preAuthQueryDetails?.ihxQueriesResponses) {
          const queryObject =
            state.preAuthQueryDetails.ihxQueriesResponses[index];
          if (queryObject) {
            const [, responses] = Object.entries(queryObject)[0];
            const response = responses.find((field) => field.id === id);
            if (response) {
              response.data = value;
            }
          }
        } else {
          const queryObject =
            state.preAuthQueryDetails.cghsQueriesResponse[index];
          if (queryObject) {
            const [, responses] = Object.entries(queryObject)[0];
            const response = responses.find((field) => field.id === id);
            if (response) {
              response.data = value;
            }
          }
        }
      }
    },
    setSelectedFile: (
      state,
      action: PayloadAction<{ file: File; documentType: string } | null>,
    ) => {
      state.selectedFile = action.payload;
    },
    setProcessedMaps: (state, action: any) => {
      state.patients = action.payload.patientsMap;
      state.pageToPatientIdMap = action.payload.pageToPatientIdMap;
    },
    updatePatientFromSSE: (
      state,
      action: PayloadAction<{
        visitId: number;
        caseId: number;
        claimId?: string;
        message?: string;
        shortMessage?: string;
        assignedToUserName?: string;
        portalStatus?: string;
        assignedTo?: any;
      }>,
    ) => {
      const {
        visitId,
        caseId,
        claimId,
        message,
        shortMessage,
        assignedToUserName,
        portalStatus,
        assignedTo,
      } = action.payload;
      const patient = state.allPatients.find(
        (patient: any) => patient.visitId === visitId,
      );
      if (patient) {
        if (patient?.caseId === caseId) {
          if (claimId) {
            patient.claimId = claimId;
          }
          if (message) {
            patient.message = message;
          }
          if (shortMessage) {
            patient.shortMessage = shortMessage;
          }
          if (assignedToUserName) {
            patient.assignedToUserName = assignedToUserName;
          }
          if (assignedTo) {
            patient.assignedTo = assignedTo;
          }
          if (portalStatus) {
            patient.portalStatus = portalStatus;
          }
        }
      }
    },
    addPatientInListFromSSE: (
      state,
      action: PayloadAction<{
        newPatient?: any;
      }>,
    ) => {
      const { newPatient } = action.payload;

      if (
        !state.pageToPatientIdMap[state.patientFilters.pageNumber][
          newPatient?.visitId
        ]
      ) {
        state.allPatients.unshift(newPatient);

        state.pageToPatientIdMap[state.patientFilters.pageNumber].unshift(
          newPatient?.visitId,
        );
      }
    },
    updatePatientInPatientList: (
      state,
      action: PayloadAction<{
        visitId: number;
        assignedToUserName?: string;
        assignedTo?: any;
      }>,
    ) => {
      const { visitId, assignedToUserName, assignedTo } = action.payload;

      const patient = state.allPatients.find(
        (patient: any) => patient.visitId === visitId,
      );
      if (patient) {
        if (assignedToUserName) {
          patient.assignedToUserName = assignedToUserName;
        }
        if (assignedTo) {
          patient.assignedTo = assignedTo;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getAllVisitStages.pending, (state, action) => {
        state.isFetchingVisitStages = true;
        state.error = null;
      })
      .addCase(getAllVisitStages.fulfilled, (state, action: any) => {
        state.isFetchingVisitStages = false;
        if (action.payload) {
          action.payload?.forEach((visitStage: IVisitStage, idx: number) => {
            if (visitStage?.id) {
              state.visitStages[visitStage?.stageLevelId] = visitStage;
            }
          });
        }
        state.error =
          action.payload.errorMessage || "Failed to fetch all visit stages";
      })
      .addCase(getAllVisitStages.rejected, (state, action) => {
        state.isFetchingVisitStages = false;
        state.error =
          action.error.message || "Failed to fetch all visit stages";
      })
      .addCase(getAllStates.pending, (state) => {
        state.isFetchingStates = true;
        state.error = null;
      })
      .addCase(getAllStates.fulfilled, (state, action: any) => {
        state.isFetchingStates = false;
        if (action.payload) {
          action.payload?.forEach((stateItem: IState, idx: number) => {
            if (stateItem?.id) {
              state.states[stateItem?.id] = stateItem;
            }
          });
        }
        state.error = action.payload.errorMessage || "Failed to fetch states";
      })
      .addCase(getAllStates.rejected, (state, action) => {
        state.isFetchingStates = false;
        state.error = action.error.message || "Failed to fetch all states";
      })
      .addCase(getAllSchemes.pending, (state) => {
        state.isFetchingSchemes = true;
        state.error = null;
      })
      .addCase(getAllSchemes.fulfilled, (state, action: any) => {
        state.isFetchingSchemes = false;
        if (action.payload.schemes) {
          action.payload.schemes?.forEach((scheme: IScheme, idx: number) => {
            if (scheme?.id) {
              state.schemes[scheme?.id] = scheme;
            }
          });
        }
        state.error = action.payload.errorMessage || "Failed to fetch schemes";
      })
      .addCase(getAllSchemes.rejected, (state, action) => {
        state.isFetchingSchemes = false;
        state.error = action.error.message || "Failed to fetch all schemes";
      })
      .addCase(getAllVisitTypes.pending, (state, action) => {
        state.isFetchingVisitTypes = true;
        state.error = null;
      })
      .addCase(getAllVisitTypes.fulfilled, (state, action: any) => {
        state.isFetchingVisitTypes = false;
        if (action.payload) {
          action.payload?.forEach((visitType: IVisitType, idx: number) => {
            if (visitType?.id) {
              state.visitTypes[visitType?.id] = visitType;
            }
          });
        }
        state.error =
          action.payload.errorMessage || "Failed to fetch all visit types";
      })
      .addCase(getAllVisitTypes.rejected, (state, action) => {
        state.isFetchingVisitTypes = false;
        state.error = action.error.message || "Failed to fetch all visit types";
      })
      .addCase(getPatients.pending, (state) => {
        state.patients = {};
        state.pageToPatientIdMap = {};
        state.isFetchingAllPatients = true;
        state.error = null;
      })
      .addCase(getPatients.fulfilled, (state, action: any) => {
        state.isFetchingAllPatients = false;
        action.payload.patientResponseList?.forEach((patient: any) => {
          if (patient?.visitInfoResponse?.id) {
            state.patients[patient?.visitInfoResponse?.id] = patient;

            if (!state.pageToPatientIdMap[state.patientFilters.pageNumber]) {
              state.pageToPatientIdMap[state.patientFilters.pageNumber] = [];
            }
            if (
              !state.pageToPatientIdMap[
                state.patientFilters.pageNumber
              ].includes(patient?.visitInfoResponse?.id)
            ) {
              state.pageToPatientIdMap[state.patientFilters.pageNumber].push(
                patient?.visitInfoResponse?.id,
              );
            }
          }
        });

        state.totalAvailablePatients = action.payload.totalElements || 0;
        state.patientFilters.pageSize = action.meta.arg.filters.pageSize;
        state.error = null;
      })
      .addCase(getPatients.rejected, (state, action) => {
        state.isFetchingAllPatients = false;
        state.error = action.error.message || "Failed to fetch patients";
      })
      .addCase(getPatientsPL.pending, (state) => {
        state.patients = {};
        state.allPatients = [];
        state.pageToPatientIdMap = {};
        state.isFetchingAllPatients = true;
        state.error = null;
      })
      .addCase(getPatientsPL.fulfilled, (state, action: any) => {
        state.isFetchingAllPatients = false;
        action.payload.stagesCount?.forEach((stageCount: IStageCount) => {
          const [stageName, count] = Object.entries(stageCount)[0];
          state.stagesCount[stageName.toUpperCase()] = {
            [stageName.toUpperCase()]: count,
          };
        });
        state.patients = {};
        state.pageToPatientIdMap = {};
        state.allPatients = action.payload.patientResponseList;

        // action.payload.patientResponseList?.forEach((patient: any) => {
        //   if (patient?.visitId) {
        //     state.patients[patient?.visitId] = patient;

        //     if (!state.pageToPatientIdMap[state.patientFilters.pageNumber]) {
        //       state.pageToPatientIdMap[state.patientFilters.pageNumber] = [];
        //     }
        //     if (
        //       !state.pageToPatientIdMap[
        //         state.patientFilters.pageNumber
        //       ].includes(patient?.visitId)
        //     ) {
        //       state.pageToPatientIdMap[state.patientFilters.pageNumber].push(
        //         patient?.visitId
        //       );
        //     }
        //   }
        // });

        state.totalAvailablePatients = action.payload.totalElements || 0;
        state.patientFilters.pageSize = action.meta.arg.filters.pageSize;
        state.error = null;
      })
      .addCase(getPatientsPL.rejected, (state, action) => {
        state.isFetchingAllPatients = false;
        state.error = action.error.message || "Failed to fetch patients";
      })
      .addCase(getPatientById.pending, (state) => {
        state.isFetchingPatientById = true;
        state.error = null;
      })
      .addCase(
        getPatientById.fulfilled,
        (state, action: PayloadAction<IPatientFetchResponse>) => {
          state.isFetchingPatientById = false;
          state.selectedPatient = action.payload.patient || null;
        },
      )
      .addCase(getPatientById.rejected, (state, action) => {
        state.isFetchingPatientById = false;
        state.error = action.error.message || "Failed to fetch patient";
      })
      .addCase(getPatientCases.pending, (state) => {
        state.isFetchingPatientCases = true;
        state.error = null;
      })
      .addCase(
        getPatientCases.fulfilled,
        (state, action: PayloadAction<IPatientCasesFetchResponse>) => {
          state.isFetchingPatientCases = false;
          state.patientToCasesMap[action.payload.patientId] =
            action.payload.cases;
        },
      )
      .addCase(getPatientCases.rejected, (state, action) => {
        state.isFetchingPatientCases = false;
        state.error = action.error.message || "Failed to fetch patient cases";
      })
      .addCase(getPatientCasesWithVisits.pending, (state) => {
        state.isFetchingPatientCasesWithVisits = true;
        state.error = null;
      })
      .addCase(
        getPatientCasesWithVisits.fulfilled,
        (
          state,
          action: PayloadAction<IPatientCasesWithVisitsFetchResponse>,
        ) => {
          state.isFetchingPatientCasesWithVisits = false;

          state.casesWithAllVisitsOfPatient[action.payload.patientId] =
            action.payload.cases;
        },
      )
      .addCase(getPatientCasesWithVisits.rejected, (state, action) => {
        state.isFetchingPatientCasesWithVisits = false;
        state.error =
          action.error.message || "Failed to fetch patient cases with visits";
      })
      .addCase(UpdatePatientDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(UpdatePatientDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pageToPatientIdMap = {};
        state.patients = {};
        if (action.payload?.patient) {
          state.patientDetails = action.payload?.patient;
        }
      })
      .addCase(UpdatePatientDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.error.message || "Failed to Update patient details";
      })
      .addCase(postPatientDetails.pending, (state) => {
        state.isPostPatientDetails = true;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        postPatientDetails.fulfilled,
        (state, action: PayloadAction<IPostPatientDetailsResponse>) => {
          state.isPostPatientDetails = false;
          state.isLoading = false;
          if (action.payload?.patient) {
            state.patientDetails = action.payload?.patient;
          }
          if (action?.payload?.patient?.id) {
            state.patients[action?.payload?.patient?.id] =
              action.payload.patient;
          }
          state.pageToPatientIdMap = {};
          state.patients = {};
          state.selectedPatient = null;
        },
      )
      .addCase(postPatientDetails.rejected, (state, action) => {
        state.isPostPatientDetails = false;
        state.isLoading = false;
        state.error = action.error.message || "Failed to post patient details";
      })
      .addCase(getPatientSchemes.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getPatientSchemes.fulfilled,
        (state, action: PayloadAction<IPatientSchemeList>) => {
          state.patientSchemes = action.payload.schemeNames || null;
        },
      )
      .addCase(getPatientSchemes.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch patient schemes";
      })
      .addCase(getBasicDetails.pending, (state) => {
        state.error = null;
        state.isFetchingPatientById = true;
      })
      .addCase(
        getBasicDetails.fulfilled,
        (state, action: PayloadAction<IFetchPatientBasicDetails>) => {
          state.fetchBasicDetails = action.payload.fetchBasicDetail || null;
          state.isFetchingPatientById = false;
        },
      )
      .addCase(getBasicDetails.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch patient details";
        state.isFetchingPatientById = false;
      })
      .addCase(getBasicDetails2.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getBasicDetails2.fulfilled,
        (state, action: PayloadAction<IFetchPatientBasicDetails>) => {
          state.fetchBasicDetails2 = action.payload.fetchBasicDetail || null;
        },
      )
      .addCase(getBasicDetails2.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch patient details";
      })
      .addCase(readOcrDocs.pending, (state) => {
        state.isOcrFetchingPatient = true;
        state.error = null;
        state.isLoading = true;
      })
      .addCase(readOcrDocs.fulfilled, (state, action: PayloadAction<any>) => {
        state.isOcrFetchingPatient = false;
        // state.patientDetails = action.payload || null;
        // state.ocrDetails = action.payload;
        if (action?.payload?.patientAndEsmInformation?.cardNo) {
          state.ocrDetails = {
            id: null,
            referralNo: action.payload.referral_information.referralNo || "",
            referralExpiryFrom:
              action.payload.referral_information.dateOfIssue || "",
            referralExpiryTo:
              action.payload.referral_information.validityUpto || "",
            referralFor: action.payload.referredTo || "",
            referringDoctor: "",
            noOfVisits: action.payload.referral_information.session || "",
            ariAndSC: false,
            ipAllowed: false,
            referralType: action.payload.referral_information.patientType || "",
            cardCategory:
              action.payload.patientAndEsmInformation.category || "",
            cardNumber: action.payload.patientAndEsmInformation.cardNo || "",
            serviceNumber:
              action.payload.patientAndEsmInformation.serviceNo || "",
            investigation:
              action.payload.provisionalDiagnosis?.investigation || "",
            clinicalNotes:
              action.payload.provisionalDiagnosis?.clinicalNotes || "",
            consultationFor:
              action.payload.provisionalDiagnosis?.consultationFor || "",
            admission: action.payload.provisionalDiagnosis?.admission || "",
            documentType: action.payload?.documentType,
            documentName: action.payload?.documentName,
            documentUrl: action.payload.documentPath,
            documentId: action.payload?.documentId,
            beneficiaryId: action.payload.beneficiaryId || "",
            age:
              action.payload.age ||
              action.payload?.patientAndEsmInformation?.age ||
              null,
            gender:
              action.payload.gender ||
              action.payload?.patientAndEsmInformation?.gender ||
              "",
            name:
              action.payload.name ||
              action.payload?.patientAndEsmInformation?.name ||
              "",
            refferenceID: action.payload.refferenceID || "",
            timeIssued: action.payload.timeIssued || "",
            tokenNumber: action.payload.tokenNumber || "",
            dateIssued: action.payload.dateIssued || "",
            referralServicesResponses: null,
            services: null,
            polyClinic: action.payload.referralFromPolyclinic,
            polyclinicRemarks: action.payload.polyclinicRemarks,
            documentLabelType: action.payload.documentLabelType,
            email:
              action.payload.email ||
              action.payload?.patientAndEsmInformation?.emailId ||
              "",
            diagnosis: action.payload.diagnosis,
          };
        } else {
          state.ocrDetails = action.payload;
          if (action?.payload?.documentLabelType == "REFERRAL LETTER") {
            state.ocrDetailsOfReferral = action.payload;
          }
        }

        state.documentDetails = {
          documentName: action.payload.imageName || action.payload.documentName,
          documentType: action.payload.documentType,
          documentUrl: action.payload.documentPath,
          documentLabelType: action.payload.documentLabelType,
          id: action.payload?.documentId,
          indexData:
            action?.payload?.services || action?.payload?.details || [],
          indexCardCategory:
            action?.payload?.cardCategory || action?.payload?.cardType,
          regNo: action?.payload?.regNo || null,
        };

        if (
          action?.payload?.documentLabelType
            ?.toLowerCase()
            ?.includes("regn slip")
        ) {
          state.ocrDetails = null;
        }
        state.isLoading = false;
      })
      .addCase(readOcrDocs.rejected, (state, action) => {
        state.isOcrFetchingPatient = false;
        state.error = action.error.message || "Failed to Read";
      })

      .addCase(
        postBeneficiaryDetailsForScheme.fulfilled,
        (
          state,
          action: any,
          // action: PayloadAction<IFetchPatientDetailsFromPortalResponse>
        ) => {
          const identityNumber = action.meta.arg?.beneficiaryId;
          state.isFetchingPatientDetails = false;
          state.patientDetails = action.payload.beneficiaryFetchDto
            ? {
                ...action.payload.beneficiaryFetchDto,
                identityNumber,
                primaryMobile: action.payload.beneficiaryFetchDto.mobile,
              }
            : null;
        },
      )
      .addCase(postBeneficiaryDetailsForScheme.rejected, (state, action) => {
        state.isFetchingPatientDetails = false;
        state.error = action.error.message || "Failed to fetch patient";
      })
      .addCase(postBeneficiaryDetailsForScheme.pending, (state) => {
        state.isFetchingPatientDetails = true;
        state.error = null;
      })

      .addCase(fetchPatientDetailsFromPortal.pending, (state) => {
        state.isFetchingPatientDetails = true;
        state.error = null;
      })
      .addCase(
        fetchPatientDetailsFromPortal.fulfilled,
        (
          state,
          action: any,
          // action: PayloadAction<IFetchPatientDetailsFromPortalResponse>
        ) => {
          const formData = action.meta.arg.formData;
          state.isFetchingPatientDetails = false;
          // state.patientDetails = action.payload.patient || null;
          state.patientDetails = action.payload.patient
            ? {
                ...action.payload.patient,
                identityNumber: formData?.patientSchemeDetails?.identityNumber,
              }
            : null;
        },
      )
      .addCase(fetchPatientDetailsFromPortal.rejected, (state, action) => {
        state.isFetchingPatientDetails = false;
        state.error = action.error.message || "Failed to fetch patient";
      })
      .addCase(fetchPatientDetailsFromAyushBPortal.pending, (state) => {
        state.isFetchingPatientDetails = true;
        state.error = null;
      })
      .addCase(
        fetchPatientDetailsFromAyushBPortal.fulfilled,
        (
          state,
          action: any,
          // action: PayloadAction<IFetchPatientDetailsFromPortalResponse>
        ) => {
          const formData = action.meta.arg.payloadData;
          state.isFetchingPatientDetails = false;
          // state.patientDetails = action.payload.patient || null;
          state.patientDetails = action.payload.patient
            ? {
                ...action.payload.patient,
                identityNumber: formData?.beneficiaryId,
              }
            : null;
        },
      )
      .addCase(
        fetchPatientDetailsFromAyushBPortal.rejected,
        (state, action) => {
          state.isFetchingPatientDetails = false;
          state.error = action.error.message || "Failed to fetch patient";
        },
      )
      .addCase(fetchPatientDetailsFromHealthIndiaPortal.pending, (state) => {
        state.isFetchingPatientDetails = true;
        state.error = null;
      })
      .addCase(
        fetchPatientDetailsFromHealthIndiaPortal.fulfilled,
        (state, action: any) => {
          const formData = action.meta.arg;
          state.isFetchingPatientDetails = false;
          const payload = action.payload?.data ?? action.payload;
          const beneficiary = payload?.beneficiaryFetchDto;
          const healthIndiaDetails = payload?.healthIndiaDetails;
          state.patientDetails =
            beneficiary || healthIndiaDetails
              ? {
                  ...beneficiary,
                  ...healthIndiaDetails,
                  identityNumber:
                    formData?.identityNumber ||
                    healthIndiaDetails?.healthIndiaId ||
                    beneficiary?.abhaNumber,
                  primaryMobile:
                    beneficiary?.mobile || healthIndiaDetails?.contactNo,
                  fullName:
                    beneficiary?.name || healthIndiaDetails?.patientName,
                }
              : null;
        },
      )
      .addCase(
        fetchPatientDetailsFromHealthIndiaPortal.rejected,
        (state, action) => {
          state.isFetchingPatientDetails = false;
          state.error = action.error.message || "Failed to fetch patient";
        },
      )
      .addCase(fetchPatientDetailsFromHeritagePortal.pending, (state) => {
        state.isFetchingPatientDetails = true;
        state.error = null;
      })
      .addCase(
        fetchPatientDetailsFromHeritagePortal.fulfilled,
        (state, action: any) => {
          const formData = action.meta.arg;
          state.isFetchingPatientDetails = false;
          const payload = action.payload?.data ?? action.payload;
          const beneficiary = payload?.beneficiaryFetchDto;
          const heritageDetails = payload?.heritageDetails;
          state.patientDetails =
            beneficiary || heritageDetails
              ? {
                  ...beneficiary,
                  ...heritageDetails,
                  identityNumber:
                    formData?.identityNumber ||
                    heritageDetails?.cardNo ||
                    beneficiary?.abhaNumber,
                  primaryMobile: beneficiary?.mobile,
                  fullName: beneficiary?.name || heritageDetails?.patientName,
                }
              : null;
        },
      )
      .addCase(
        fetchPatientDetailsFromHeritagePortal.rejected,
        (state, action) => {
          state.isFetchingPatientDetails = false;
          state.error = action.error.message || "Failed to fetch patient";
        },
      )
      .addCase(fetchPatientDetailsFromStarHealthPortal.pending, (state) => {
        state.isFetchingPatientDetails = true;
        state.error = null;
      })
      .addCase(
        fetchPatientDetailsFromStarHealthPortal.fulfilled,
        (state, action: any) => {
          const formData = action.meta.arg;
          state.isFetchingPatientDetails = false;
          const payload = action.payload?.data ?? action.payload;
          const beneficiary = payload?.beneficiaryFetchDto;
          const starHealthDetails = payload?.starHealthDetails;
          state.patientDetails =
            beneficiary || starHealthDetails
              ? {
                  ...beneficiary,
                  ...starHealthDetails,
                  identityNumber:
                    formData?.identityNumber ||
                    starHealthDetails?.medicalCardNumber ||
                    beneficiary?.abhaNumber,
                  primaryMobile:
                    beneficiary?.mobile || starHealthDetails?.mobile,
                  fullName: beneficiary?.name || starHealthDetails?.patientName,
                  sex: starHealthDetails?.gender || beneficiary?.gender,
                  policyType: starHealthDetails?.policyType,
                  policySource: starHealthDetails?.policySource,
                  employeeId: starHealthDetails?.employeeId,
                  policyStartDate: starHealthDetails?.policyStartDate,
                  policyEndDate: starHealthDetails?.policyEndDate,
                  derivedGroupPolicy: starHealthDetails?.derivedGroupPolicy,
                  policyNumber: starHealthDetails?.policyNumber,
                  dob: starHealthDetails?.dob,
                }
              : null;
        },
      )
      .addCase(
        fetchPatientDetailsFromStarHealthPortal.rejected,
        (state, action) => {
          state.isFetchingPatientDetails = false;
          state.error = action.error.message || "Failed to fetch patient";
        },
      )
      .addCase(fetchPatientDetailsFromFhplPortal.pending, (state) => {
        state.isFetchingPatientDetails = true;
        state.error = null;
      })
      .addCase(
        fetchPatientDetailsFromFhplPortal.fulfilled,
        (state, action: any) => {
          const formData = action.meta.arg;
          state.isFetchingPatientDetails = false;
          const fhplDetails = action.payload?.data ?? action.payload;

          if (!fhplDetails || typeof fhplDetails !== "object") {
            state.patientDetails = null;
            return;
          }

          const parsedAge =
            fhplDetails?.age != null && String(fhplDetails.age).trim() !== ""
              ? parseInt(String(fhplDetails.age).replace(/[^\d]/g, ""), 10)
              : null;

          state.patientDetails = {
            ...fhplDetails,
            identityNumber:
              formData?.uhid ||
              fhplDetails?.healthIndiaId ||
              fhplDetails?.employeeCode ||
              null,
            employeeId: fhplDetails?.employeeCode ?? null,
            primaryMobile: fhplDetails?.contactNo || null,
            fullName: fhplDetails?.patientName ?? null,
            name: fhplDetails?.patientName ?? null,
            age: Number.isNaN(parsedAge)
              ? (fhplDetails?.age ?? null)
              : parsedAge,
            sex: fhplDetails?.gender ?? null,
            gender: fhplDetails?.gender ?? null,
            emailId: fhplDetails?.emailId ?? null,
            email: fhplDetails?.emailId ?? null,
            policySource: fhplDetails?.insuranceCompany ?? null,
            policyNumber: fhplDetails?.policyNumber || null,
            policyType: fhplDetails?.policyType ?? null,
            policyStartDate: fhplDetails?.policyStartDate ?? null,
            policyEndDate: fhplDetails?.policyEndDate ?? null,
            derivedGroupPolicy: false,  //fhplDetails?.familyCode
            dob: fhplDetails?.policyInceptionDate ?? null,
          };
        },
      )
      .addCase(fetchPatientDetailsFromFhplPortal.rejected, (state, action) => {
        state.isFetchingPatientDetails = false;
        state.error = action.error.message || "Failed to fetch patient";
      })

      .addCase(getPreAuthQueryDetails.pending, (state) => {
        state.isFetchingPreAuthQueryDetails = true;
        state.error = null;
      })
      .addCase(
        getPreAuthQueryDetails.fulfilled,
        (state, action: PayloadAction<IPatientPreAuthQueryGetDetails>) => {
          state.isFetchingPreAuthQueryDetails = false;
          state.preAuthQueryDetails = action.payload.queryData;
        },
      )
      .addCase(getPreAuthQueryDetails.rejected, (state, action) => {
        state.isFetchingPreAuthQueryDetails = false;
        state.error =
          action.error.message || "Failed to fetch pre-auth query details";
      })
      .addCase(downloadReportScheme.pending, (state) => {
        state.isDownloadingReport = true;
        state.error = null;
      })
      .addCase(downloadReportScheme.fulfilled, (state) => {
        state.isDownloadingReport = false;
      })
      .addCase(downloadReportScheme.rejected, (state, action) => {
        state.isDownloadingReport = false;
      })
      .addCase(downloadReportSchemeNew.pending, (state) => {
        state.isDownloadingReport = true;
        state.error = null;
      })
      .addCase(downloadReportSchemeNew.fulfilled, (state) => {
        state.isDownloadingReport = false;
      })
      .addCase(downloadReportSchemeNew.rejected, (state, action) => {
        state.isDownloadingReport = false;
      })
      .addCase(getPatientReferral.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getPatientReferral.fulfilled,
        (state, action: PayloadAction<Array<IDocumentFields>>) => {
          state.referrals = {};
          action.payload?.forEach((referral: IDocumentFields) => {
            if (
              referral?.id &&
              referral?.referenceType?.toLowerCase()?.includes("referral")
            ) {
              state.referrals[referral.id] = referral;
            }
          });
          state.error = null;
        },
      )
      .addCase(getPatientReferral.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch patients";
      })
      .addCase(getPatientListCounts.pending, (state) => {
        state.error = null;
        state.patientListPendingCounts = null;
      })
      .addCase(
        getPatientListCounts.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.patientListPendingCounts = action.payload;
          state.error = null;
        },
      )
      .addCase(getPatientListCounts.rejected, (state, action) => {
        state.patientListPendingCounts = null;
        state.error = action.error.message || "Failed to fetch patients";
      })
      .addCase(getDeficiencyQuestions.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getDeficiencyQuestions.fulfilled,
        (state, action: PayloadAction<Array<IDeficiencyCheck>>) => {
          state.deficiencyCheckQuestions = action.payload;
        },
      )
      .addCase(getDeficiencyQuestions.rejected, (state, action) => {
        state.isDownloadingReport = false;
      })
      .addCase(getDeficiencyQuestionData.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getDeficiencyQuestionData.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (typeof action.payload === "object") {
            state.deficiencyCheckQuestions = action.payload;
          }
        },
      )
      .addCase(getDeficiencyQuestionData.rejected, (state, action) => {
        state.isDownloadingReport = false;
      })
      .addCase(postDeficiencyQuestion.pending, (state) => {
        state.error = null;
      })
      .addCase(
        postDeficiencyQuestion.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (typeof action.payload === "object") {
            state.deficiencyCheckQuestions = action.payload;
          }
        },
      )
      .addCase(postDeficiencyQuestion.rejected, (state, action) => {
        state.isDownloadingReport = false;
      })
      .addCase(getPatientDetailsUsingUHID.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.uhidNo = "";
      })
      .addCase(getPatientDetailsUsingUHID.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pageToPatientIdMap = {};
        state.patientDetails = null;
        state.referrals = {};
        state.documentDetails = null;
        if (typeof action.payload === "object") {
          state.patientDetails = action.payload;
          if (action.payload.patientDocuments.length > 0) {
            state.documentDetails = {
              ...action.payload.patientDocuments?.[0],
              documentName: action.payload.patientDocuments?.[0]?.documentName,
              documentType: action.payload.patientDocuments?.[0]?.documentType,
              documentUrl: action.payload.patientDocuments?.[0]?.documentUrl,
              documentLabelType:
                action.payload.patientDocuments?.[0]?.documentLabelType,
              // referenceType:
              //   action.payload.patientDocuments?.[0]?.PatientDocuments,
              id: action.payload.patientDocuments?.[0]?.id,
            };
          }

          action.payload?.referralDocuments?.forEach(
            (referral: IDocumentFields) => {
              if (
                referral?.id &&
                referral?.documentLabelType?.toLowerCase()?.includes("referral")
              ) {
                state.referrals[referral.id] = referral;
              }
            },
          );
        } else {
          // errorMessage("No Patient Found");
        }
      })
      .addCase(getPatientDetailsUsingUHID.rejected, (state, action) => {
        const formData = action.meta.arg.formData;
        state.isLoading = false;
        state.patientDetails = null;
        // state.patientDetails = formData;
        state.referrals = {};
        state.uhidNo = formData?.uhidNo;
        state.documentDetails = null;
        state.error = action.error.message || "Failed to fetch patient details";
        // errorMessage("No Patient Found");
      })
      .addCase(uploadDocument.pending, (state) => {
        state.error = null;
        state.isLoading = true;
      })
      .addCase(
        uploadDocument.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.documentDetails = null;
          state.documentDetails = {
            documentName:
              action.payload.imageName || action.payload.documentName,
            documentType: action.payload.documentType,
            documentUrl:
              action.payload.documentPath || action.payload.documentUrl,
            documentLabelType: action.payload.documentLabelType,
            id: action.payload?.id,
          };
          state.isLoading = false;
        },
      )
      .addCase(uploadDocument.rejected, (state, action: any) => {
        state.error = action.error.message || "Failed to Upload Document";
        // state.documentDetails = {
        //   documentName: action.payload.imageName || action.payload.documentName,
        //   documentType: action.payload.documentType,
        //   documentUrl:
        //     action.payload.documentPath || action.payload.documentUrl,
        //   documentLabelType: action.payload.documentLabelType,
        //   id: action.payload?.id,
        // };
      })
      .addCase(getAllDetailsCount.rejected, (state, action) => {
        state.allDetailsCount = null;
      })
      .addCase(getAllDetailsCount.pending, (state) => {
        state.allDetailsCount = null;
      })
      .addCase(
        getAllDetailsCount.fulfilled,
        (state, action: PayloadAction<any>) => {
          if (typeof action.payload === "object") {
            state.allDetailsCount = action.payload;
          }
        },
      )
      .addCase(getTimelineDetails.rejected, (state, action) => {
        state.isFetchingTimeLine = false;
        state.timelineDetails = [];
      })
      .addCase(getTimelineDetails.pending, (state) => {
        state.isFetchingTimeLine = true;
        state.timelineDetails = [];
      })
      .addCase(
        getTimelineDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isFetchingTimeLine = false;
          if (typeof action.payload === "object") {
            // const ascOrderData = action?.payload?.sort((a: any, b: any) => a.id - b.id);
            state.timelineDetails = action.payload;
          }
        },
      )
      .addCase(fetchShiftPatientDetails.pending, (state) => {
        state.error = null;
        state.shiftPatientData = [];
      })
      .addCase(
        fetchShiftPatientDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.shiftPatientData = action.payload;
        },
      )
      .addCase(fetchShiftPatientDetails.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to fetch patient shift data";
        state.shiftPatientData = [];
      })
      .addCase(submitShiftPatientDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        submitShiftPatientDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
        },
      )
      .addCase(submitShiftPatientDetails.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateShiftPatientDetails.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        updateShiftPatientDetails.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
        },
      )
      .addCase(updateShiftPatientDetails.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(getSchemeMetaData.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        getSchemeMetaData.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.schemeMetaData = action.payload;
        },
      )
      .addCase(getSchemeMetaData.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(processPatientsPLData.fulfilled, (state, action) => {
        state.patients = { ...state.patients, ...action.payload.patientsMap };
        state.pageToPatientIdMap = {
          ...state.pageToPatientIdMap,
          ...action.payload.pageToIdMap,
        };
      });
  },
});

export const {
  clearFetchBasicDetail,
  updateSelectedPatient,
  setPatientFilters,
  setRetainedPatientFilters,
  clearPageToPatientIdMap,
  clearPatientDetails,
  resetPatientToCasesMap,
  updatePreAuthQueryField,
  setSelectedFile,
  clearOcrDetails,
  clearDeficiencyCheck,
  clearPatientReferrals,
  clearUhiNo,
  swapReadOcrDetails,
  clearOcrDetailsOfReferral,
  updatePatientFromSSE,
  addPatientInListFromSSE,
  updatePatientInPatientList,
} = patientSlice.actions;
export default patientSlice.reducer;
